import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import axios from 'axios';
import '../styles/recovery-design-system.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isEU, setIsEU] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,  // Always true
    analytics: false,
    marketing: false,
    preferences: false
  });

  useEffect(() => {
    checkConsent();
  }, []);

  const checkConsent = async () => {
    // Check if consent already given
    const consent = localStorage.getItem('cookie-consent');
    if (consent) {
      const consentData = JSON.parse(consent);
      setPreferences(consentData);
      applyConsent(consentData);
      return;
    }

    // Check if visitor is from EU
    try {
      const response = await axios.get(`${BACKEND_URL}/api/geo/detect`);
      const location = response.data.location;
      
      setIsEU(location.is_eu);
      
      // Show banner if EU visitor or no consent given
      if (location.is_eu) {
        setShowBanner(true);
      } else {
        // Non-EU: Accept all by default
        const defaultConsent = {
          necessary: true,
          analytics: true,
          marketing: true,
          preferences: true
        };
        saveConsent(defaultConsent);
      }
    } catch (error) {
      console.error('Failed to detect location:', error);
      // Show banner on error (safer for GDPR)
      setShowBanner(true);
    }
  };

  const applyConsent = (consent) => {
    // Apply consent preferences to tracking scripts
    
    if (consent.analytics) {
      // Enable Google Analytics or your analytics tool
      // window.gtag('consent', 'update', { 'analytics_storage': 'granted' });
    }

    if (consent.marketing) {
      // Enable marketing pixels (Facebook, etc.)
      // window.fbq('consent', 'grant');
    }

    if (consent.preferences) {
      // Enable preference cookies
    }
  };

  const saveConsent = async (consentData) => {
    try {
      // Save to localStorage
      localStorage.setItem('cookie-consent', JSON.stringify(consentData));
      
      // Save to backend for GDPR compliance
      await axios.post(`${BACKEND_URL}/api/consent/save`, consentData);
      
      // Apply consent
      applyConsent(consentData);
      
      // Hide banner
      setShowBanner(false);
      setShowPreferences(false);
    } catch (error) {
      console.error('Failed to save consent:', error);
    }
  };

  const handleAcceptAll = () => {
    const allConsent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    };
    setPreferences(allConsent);
    saveConsent(allConsent);
  };

  const handleRejectAll = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    };
    setPreferences(minimalConsent);
    saveConsent(minimalConsent);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 99999,
          background: 'white',
          boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          borderTop: '3px solid var(--sage)'
        }}
      >
        {!showPreferences ? (
          // Main Cookie Banner
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '1.5rem 2rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '1.5rem',
              flexWrap: 'wrap'
            }}>
              {/* Icon */}
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, var(--sage) 0%, var(--sage-dark) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                flexShrink: 0
              }}>
                <Cookie className="w-6 h-6" />
              </div>

              {/* Content */}
              <div style={{ flex: 1, minWidth: '300px' }}>
                <h3 style={{
                  fontFamily: 'var(--font-heading)',
                  color: 'var(--navy)',
                  fontSize: '1.25rem',
                  marginBottom: '0.5rem',
                  fontWeight: '600'
                }}>
                  {isEU ? 'We Value Your Privacy' : 'Cookie Notice'}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.9375rem',
                  lineHeight: '1.6',
                  marginBottom: 0
                }}>
                  We use cookies to enhance your experience, analyze site traffic, and personalize content. 
                  {isEU && ' By clicking "Accept All", you consent to our use of cookies in accordance with GDPR.'} 
                  {' '}
                  <a href="/privacy" style={{ color: 'var(--sage)', textDecoration: 'underline' }}>
                    Learn more
                  </a>
                </p>
              </div>

              {/* Actions */}
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <button
                  onClick={() => setShowPreferences(true)}
                  className="btn-secondary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9375rem',
                    padding: '0.625rem 1rem'
                  }}
                >
                  <Settings className="w-4 h-4" />
                  Preferences
                </button>

                {isEU && (
                  <button
                    onClick={handleRejectAll}
                    className="btn-secondary"
                    style={{
                      fontSize: '0.9375rem',
                      padding: '0.625rem 1rem'
                    }}
                  >
                    Reject All
                  </button>
                )}

                <button
                  onClick={handleAcceptAll}
                  className="btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    fontSize: '0.9375rem',
                    padding: '0.625rem 1.5rem'
                  }}
                >
                  <Check className="w-4 h-4" />
                  Accept All
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Cookie Preferences Panel
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            padding: '2rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{
                fontFamily: 'var(--font-heading)',
                color: 'var(--navy)',
                fontSize: '1.5rem',
                margin: 0
              }}>
                Cookie Preferences
              </h3>
              <button
                onClick={() => setShowPreferences(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--text-muted)',
                  padding: '0.25rem'
                }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Necessary Cookies */}
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{
                      fontWeight: '600',
                      color: 'var(--navy)',
                      marginBottom: '0.25rem'
                    }}>
                      Necessary Cookies
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      margin: 0
                    }}>
                      Required for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <div style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--sage-light)',
                    borderRadius: '6px',
                    color: 'var(--sage-dark)',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}>
                    Always Active
                  </div>
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontWeight: '600',
                      color: 'var(--navy)',
                      marginBottom: '0.25rem'
                    }}>
                      Analytics Cookies
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      margin: 0
                    }}>
                      Help us understand how visitors interact with our website.
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* Marketing Cookies */}
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontWeight: '600',
                      color: 'var(--navy)',
                      marginBottom: '0.25rem'
                    }}>
                      Marketing Cookies
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      margin: 0
                    }}>
                      Used to deliver personalized ads and measure campaign performance.
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* Preference Cookies */}
              <div className="card" style={{ padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{
                      fontWeight: '600',
                      color: 'var(--navy)',
                      marginBottom: '0.25rem'
                    }}>
                      Preference Cookies
                    </h4>
                    <p style={{
                      fontSize: '0.875rem',
                      color: 'var(--text-muted)',
                      margin: 0
                    }}>
                      Remember your settings and preferences (language, region, etc.).
                    </p>
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={preferences.preferences}
                      onChange={(e) => setPreferences({ ...preferences, preferences: e.target.checked })}
                      style={{
                        width: '20px',
                        height: '20px',
                        cursor: 'pointer'
                      }}
                    />
                  </label>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div style={{
              marginTop: '1.5rem',
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowPreferences(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePreferences}
                className="btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <Check className="w-4 h-4" />
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
